import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import { DeviceType } from '@/src/types/deviceType'
import { Box } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'

import useCouponTray from '@/src/hooks/itinerary/useCouponTray'

import confettiAnimation from '@/src/assets/animations/confetti.json'
import CashbackScheduleModalWrapper from './cashbackScheduleModal'
import CouponTray from './couponTray'
import CouponSuccessModalWrapper from './successModal'

interface CouponTrayWrapperProps {
    deviceType: DeviceType
    onSuccessfulApply?: (message: string) => void
}

const CouponTrayWrapper = ({ deviceType, onSuccessfulApply }: CouponTrayWrapperProps) => {
    // ✅ ALL business logic here - following CODEBASE_CONTEXT guidelines
    const isMobile = deviceType === 'MOBILE'

    // Handle successful coupon application
    const handleSuccessfulApply = useCallback(
        (message: string) => {
            if (isMobile) {
                // For mobile, just pass the message to parent (mobile drawer handles modal)
                onSuccessfulApply?.(message)
            } else {
                // For desktop, show success modal
                setSuccessMessage(message)
                setSuccessModalOpen(true)
            }
        },
        [isMobile, onSuccessfulApply]
    )

    const couponTray = useCouponTray(handleSuccessfulApply)
    const tCommon = useTranslations('common')
    const locale = useLocale() || 'en'

    const [cashbackModalOpen, setCashbackModalOpen] = useState(false)
    const [knowMoreDrawerOpen, setKnowMoreDrawerOpen] = useState(false)
    const [knowMoreUrl, setKnowMoreUrl] = useState<string>('')
    const [successModalOpen, setSuccessModalOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')

    const appendLngParam = useCallback(
        (url: string) => {
            const lng = locale
            const base = typeof window !== 'undefined' ? window.location.origin : undefined
            const u = new URL(url, base)
            u.searchParams.set('lng', lng)
            return u.toString()
        },
        [locale]
    )

    const handleOpenCashbackModal = useCallback(() => {
        setCashbackModalOpen(true)
    }, [])

    const handleCloseCashbackModal = useCallback(() => {
        setCashbackModalOpen(false)
    }, [])
    const handleKnowMore = useCallback(
        (offerId: string) => {
            const offer = (couponTray.offers || []).find(o => o.id === offerId)
            const link = offer?.knowMoreLink
            if (!link) return

            const urlWithLng = appendLngParam(link)

            if (isMobile) {
                setKnowMoreUrl(urlWithLng)
                setKnowMoreDrawerOpen(true)
            } else {
                if (typeof window !== 'undefined') {
                    window.open(urlWithLng, '_blank', 'noopener,noreferrer')
                }
            }
        },
        [couponTray.offers, isMobile, appendLngParam]
    )
    const closeKnowMoreDrawer = useCallback(() => setKnowMoreDrawerOpen(false), [])

    const handleSuccessModalClose = useCallback(() => {
        setSuccessModalOpen(false)
        setSuccessMessage('')
    }, [])

    return (
        <>
            <CouponTray
                {...couponTray}
                locale={locale} // ✅ Pass locale to JSX Layer for direct data access
                isMobile={isMobile}
                tCommon={tCommon}
                onOpenCashbackModal={handleOpenCashbackModal}
                onKnowMore={handleKnowMore}
            />
            {isMobile && couponTray.cashbackSchedule && (
                <CashbackScheduleModalWrapper
                    open={cashbackModalOpen}
                    onClose={handleCloseCashbackModal}
                    cashbackSchedule={couponTray.cashbackSchedule}
                />
            )}
            {isMobile && (
                <SwipeableDrawerWithBackNavigation open={knowMoreDrawerOpen} onClose={closeKnowMoreDrawer}>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        {knowMoreUrl && (
                            <iframe
                                src={knowMoreUrl}
                                style={{ border: 'none', width: '100%', height: '100%' }}
                                title="Know more"
                            />
                        )}
                    </Box>
                </SwipeableDrawerWithBackNavigation>
            )}

            {/* Success Modal - Shows only for desktop */}
            {!isMobile && (
                <CouponSuccessModalWrapper
                    open={successModalOpen}
                    successMessage={successMessage}
                    onClose={handleSuccessModalClose}
                    animationData={confettiAnimation}
                    autoCloseDelay={2500}
                />
            )}
        </>
    )
}

export default CouponTrayWrapper
